import React, { useState, useEffect } from 'react';
import { ArrowUpRight, Linkedin, Mail, Moon, Sun, Menu, X } from 'lucide-react';
import ContactForm from './ContactForm.tsx';
import Seo from './Seo.tsx';
import Clients from './Clients.tsx';
import Testimonials from './Testimonials.tsx';

const AboutPage: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('darkMode');
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (stored === 'true' || stored === 'false') {
      setIsDarkMode(stored === 'true');
    } else {
      setIsDarkMode(mediaQuery.matches);
    }
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    const newValue = !isDarkMode;
    setIsDarkMode(newValue);
    localStorage.setItem('darkMode', newValue.toString());
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className={`min-h-screen bg-neutral-50 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 font-sans ${isLoaded ? 'animate-fadeInUp' : 'opacity-0'}`}>
      <Seo
        title="About Ben Smith | Developer"
        description="Developer of touchscreen kiosks, interactive installations, and editorial WordPress platforms. 15 years and 150+ projects for agencies, universities, and brands including John Deere and the National Park Service."
        path="/about"
      />

      {/* Navigation */}
      <nav className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md py-4 px-6 sticky top-0 shadow-soft z-50 border-b border-primary-100 dark:border-neutral-700">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <a href="/" className="text-2xl font-bold">
            <span className="text-neutral-900 dark:text-neutral-100">Ben Smith</span>
            <span className="text-primary-600 dark:text-primary-400"> Development</span>
          </a>
          <div className="hidden md:flex space-x-8 items-center">
            <a href="/" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Home
            </a>
            <a href="/#projects" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Projects
            </a>
            <a href="/kiosks" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Kiosks
            </a>
            <a href="/gutenberg" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Gutenberg
            </a>
            <a href="/maintenance-and-seo" className="whitespace-nowrap text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Maintenance &amp; SEO
            </a>
            <a href="/about" className="text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              About
            </a>
            <button onClick={() => scrollToSection('contact')} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Contact
            </button>
            <button
              onClick={toggleDarkMode}
              className="transition-all duration-200 font-medium hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-3 py-1.5"
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
          <button
            className="md:hidden p-2 hover:bg-primary-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={24} className="text-neutral-700 dark:text-neutral-300" /> : <Menu size={24} className="text-neutral-700 dark:text-neutral-300" />}
          </button>
        </div>
        {isMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border-b border-primary-100 dark:border-neutral-700 p-4 shadow-soft rounded-b-2xl z-50">
            <div className="flex flex-col space-y-4">
              <a href="/" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>Home</a>
              <a href="/#projects" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>Projects</a>
              <a href="/kiosks" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>Kiosks</a>
              <a href="/gutenberg" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>Gutenberg</a>
              <a href="/maintenance-and-seo" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>Maintenance &amp; SEO</a>
              <a href="/about" className="text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>About</a>
              <button onClick={() => scrollToSection('contact')} className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md">Contact</button>
              <button
                onClick={() => { toggleDarkMode(); toggleMenu(); }}
                className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md flex items-center space-x-2"
              >
                {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                <span>Theme</span>
              </button>
            </div>
          </div>
        )}
      </nav>

      <main>
        {/* About Hero */}
        <section className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-12">
              <div className="md:w-1/3">
                <div className="rounded-full w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 overflow-hidden mx-auto shadow-soft ring-4 ring-primary-100 dark:ring-primary-800 ring-offset-4 dark:ring-offset-neutral-900">
                  <img
                    src="/ben.jpeg"
                    alt="Benjamin Smith"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="md:w-2/3">
                <h1 className="text-3xl md:text-4xl font-bold mb-8 text-primary-700 dark:text-gray-100">About</h1>
                <p className="text-lg mb-6 dark:text-neutral-200">
                  I'm a developer in Wisconsin. Since 2010 I've shipped 150+ projects, mostly as the technical partner behind agencies: WordPress platforms, native kiosk apps, interactive installations, and the occasional Shopify store.
                </p>
                <p className="text-lg mb-6 dark:text-neutral-200">
                  Right now that means leading development on destination marketing platforms at KHM Agency — Discover Atlanta, The Palm Beaches, Visit Buffalo Niagara — and running Speed Read AI, a script-coverage tool I founded for film and publishing teams.
                </p>
                <p className="text-lg mb-8 dark:text-neutral-200">
                  The AI work is practical, not decorative: translation pipelines, generated narration, RAG systems — used where they save clients real money, like the 12-language build for the Lincoln Home historic site that eliminated its translation subscriptions.
                </p>
                <div className="flex space-x-4">
                  <a
                    href="https://www.linkedin.com/in/benjaminchristophersmith/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-neutral-900 rounded-full p-2"
                    aria-label="LinkedIn Profile"
                  >
                    <Linkedin size={28} />
                  </a>
                  <a
                    href="mailto:ben@bensmithdev.com"
                    className="text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-neutral-900 rounded-full p-2"
                    aria-label="Email ben@bensmithdev.com"
                  >
                    <Mail size={28} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Clients Section */}
        <section className="bg-white dark:bg-neutral-800">
          <Clients />
        </section>

        {/* Testimonials Section */}
        <Testimonials />

        {/* Contact Section */}
        <section id="contact" className="py-24 px-6 bg-white dark:bg-neutral-800">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center text-primary-700 dark:text-gray-100">Contact</h2>
            <p className="text-lg text-neutral-500 dark:text-neutral-400 text-center mb-12 max-w-2xl mx-auto">
              Tell me what you're building.
            </p>
            <div className="bg-white dark:bg-neutral-800 p-8 rounded-2xl shadow-soft max-w-2xl mx-auto border border-primary-100 dark:border-neutral-700">
              <ContactForm />
            </div>
            <div className="text-center mt-12">
              <p className="text-lg dark:text-neutral-200">Or email me directly:</p>
              <a href="mailto:ben@bensmithdev.com" className="text-primary-600 dark:text-primary-400 font-semibold hover:text-accent-600 dark:hover:text-accent-400 transition-colors duration-200">ben@bensmithdev.com</a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-neutral-900 dark:bg-black text-white py-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="mb-6 md:mb-0">
                <a href="/" className="text-2xl font-bold">
                  <span className="text-white">Ben Smith</span>
                  <span className="text-orange-500"> Development</span>
                </a>
                <p className="mt-2 text-primary-200 dark:text-primary-300">Web platforms, kiosk software, interactive installations.</p>
              </div>
              <div className="flex space-x-6">
                <a
                  href="https://www.linkedin.com/in/benjaminchristophersmith/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-300 dark:text-primary-400 hover:text-accent-300 dark:hover:text-accent-400 transition-all duration-200 hover:scale-110 rounded-full p-2"
                  aria-label="LinkedIn Profile"
                >
                  <Linkedin size={24} />
                </a>
                <a
                  href="mailto:ben@bensmithdev.com"
                  className="text-primary-300 dark:text-primary-400 hover:text-accent-300 dark:hover:text-accent-400 transition-all duration-200 hover:scale-110 rounded-full p-2"
                  aria-label="Email ben@bensmithdev.com"
                >
                  <Mail size={24} />
                </a>
              </div>
            </div>
            <div className="border-t border-primary-700/30 dark:border-primary-600/30 mt-8 pt-8 text-center text-primary-300 dark:text-primary-400">
              <p>&copy; {new Date().getFullYear()} Benjamin Smith. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default AboutPage;
